import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import { ReportFooterTimestamp, tableCellStyle } from '../../../utils/reportUtils';
import { formatDate } from '../../../utils/dateUtils';
import { ClinicHeader } from '../ClinicHeader';
import PatientDetailsHeader from '../PatientDetailsHeader';

interface Patient {
  id: number;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
}

interface LaboratoryTest {
  id: number;
  identifier: number;
  name: string;
  icon_url: string;
  unit: string;
  categories: { id: number; name: string; icon_url: string; gender: string; test_ids: number[]; }[];
}

interface Investigation {
  id: number;
  patient: Patient;
  laboratory_test: LaboratoryTest;
  remark: string | null;
  created_on: string;
  doctor_first_name: string;
  doctor_last_name: string;
  results: string | null;
  range: string | null;
}

interface Props {
  investigations: Investigation[];
}

const InvestigationReport: React.FC<Props> = ({ investigations }) => {
  const { t: tGeneral } = useTranslation('general');

  return (
    <Box sx={{ p: 5, backgroundColor: '#fff', color: '#333', fontFamily: 'Arial, sans-serif' }}>
      <ClinicHeader />
      <PatientDetailsHeader />
      <Box component="hr" sx={{ my: 3, borderColor: '#ccc' }} />
      <Typography variant="h6" sx={{ mb: 1, color: '#444' }}> {tGeneral('investigationTests')}</Typography>

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                tGeneral('serialNumber'),
                tGeneral('testName'),
                tGeneral('testId'),
                tGeneral('category'),
                tGeneral('doctor'),
                tGeneral('date'),
                tGeneral('result'),
                tGeneral('remark')
              ].map((heading, i) => (
                <TableCell key={i} sx={{ ...tableCellStyle, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {investigations.map((inv, i) => (
              <TableRow key={inv.id}>
                <TableCell sx={tableCellStyle}>{i + 1}</TableCell>
                <TableCell sx={tableCellStyle}>{inv.laboratory_test.name}</TableCell>
                <TableCell sx={tableCellStyle}>{inv.laboratory_test.identifier}</TableCell>
                <TableCell sx={tableCellStyle}>{inv.laboratory_test.categories?.[0]?.name || '-'}</TableCell>
                <TableCell sx={tableCellStyle}>{`${inv.doctor_first_name} ${inv.doctor_last_name}`}</TableCell>
                <TableCell sx={tableCellStyle}>
                  {formatDate(inv.created_on) || '-'}
                </TableCell>
                <TableCell sx={tableCellStyle}>{inv.results ?? '-'}</TableCell>
                <TableCell sx={tableCellStyle}>{inv.remark ?? '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ReportFooterTimestamp />
    </Box>
  );
};

export default InvestigationReport;
